// @flow

import React from 'react';
import ActionsColumn from 'speed-js-react-web/src/components/grommet2/tables/columns/Action/ActionsColumn';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import ListPage from 'esm-web-pro/containers/pages/ListPage';
import { BooleanColumn } from 'speed-js-react-web';
import { bindActionCreators } from 'redux';
import { addBreadcrumbs, removeBreadcrumb } from 'esm-web-pro/redux/breadcrumb';
import { connect, useDispatch } from 'react-redux';
import withRouter from 'speed-js-react/src/router/withRouter';
import { APIContext } from 'speed-js-react';
import QuickViewIcon from 'esm-web-pro/components/icons/QuickViewIcon';
import ColumnSettingsFormComponent from 'esm-web-pro/containers/tables/ColumnSettingsFormComponent';
import NomenclatureItemSidePanel from 'esm-web-pro/components/pages/nomenclatures/sidepanel/NomenclatureItemSidePanel';
import NomenclatureTypeSidePanel from 'esm-web-pro/components/pages/nomenclatures/sidepanel/NomenclatureTypeSidePanel';

const NOMENCLATURE_SIDEPANEL_KEY = 'NOMENCLATURE_TYPE_ID';

type Props = {
    match: Object,
    addBreadcrumbs: (title: string, link: string) => void,
    removeBreadcrumb: (title: string) => void,
};

const messages = defineMessages({
    nomenclatures: {
        id: 'adminpage-nomenclature-label-plural',
        defaultMessage: 'Nomenclatures',
    },
});

const NomenclatureTypeDashboard = (props: Props) => {
    const listRef = React.useRef(null);

    const reloadData = () => {
        if (listRef.current && listRef.current.table.current) {
            listRef.current.table.current.reloadData();
        }
    };

    const getTitle = (obj) => {
        if (obj.translations.bg) {
            return obj.translations.bg.name;
        }
        return '';
    };

    const {
        match: { params: { id: objectId } }, addBreadcrumbs: addBreadcrumbsAction,
        removeBreadcrumb: removeBreadcrumbAction,
    } = props;

    const [nomenclatureType, setNomenclatureType] = React.useState(null);
    const [nomenclatureItemFields, setNomenclatureItemFields] = React.useState(null);

    const { apiClient } = React.useContext(APIContext);
    const dispatch = useDispatch();
    const { formatMessage } = useIntl();

    const reloadNomenclatureType = React.useCallback(() => {
        apiClient.resources.nomenclatureType.items.schema(objectId).then((schema) => {
            setNomenclatureItemFields(schema.fields);
            apiClient.resources.nomenclatureType.get(objectId).then((response) => {
                setNomenclatureType(response);
            });
        });
    }, [apiClient, objectId]);

    React.useEffect(() => {
        reloadNomenclatureType();
    }, [reloadNomenclatureType]);

    React.useEffect(() => {
        if (nomenclatureType) {
            dispatch(addBreadcrumbsAction([
                {
                    title: formatMessage(messages.nomenclatures),
                    link: '/administration/nomenclatures/types/',
                },
                {
                    title: nomenclatureType.translations.bg.name,
                    link: `/administration/nomenclatures/types/${objectId}/`,
                },
            ]));
        }
        return () => {
            if (nomenclatureType) {
                dispatch(removeBreadcrumbAction(nomenclatureType.translations.bg.name));
                dispatch(removeBreadcrumbAction(formatMessage(messages.nomenclatures)));
            }
        };
    }, [addBreadcrumbsAction, removeBreadcrumbAction, dispatch, nomenclatureType, objectId, formatMessage]);

    const columns = React.useMemo(() => {
        const actions = [{
            label: <FormattedMessage
                id="projmanag-counterparties-quickview"
                defaultMessage="Quick View"
            />,
            type: 'quickView',
            accessor: 'id',
            resourceArguments: [objectId],
            component: NomenclatureItemSidePanel,
            resourceName: 'nomenclatureItemNested',
            icon: <QuickViewIcon />,
            to: '/{id}/quick_view',
            getTitle: (obj) => getTitle(obj),
            panelId: NOMENCLATURE_SIDEPANEL_KEY,
            reload: reloadData,
            extraProps: {
                nomenclatureItemFields,
            },
        }];

        const cols = [
            {
                Header: <FormattedMessage id="name" defaultMessage="Name" />,
                accessor: 'translations.bg.name',
                orderBy: 'name',
                minWidth: 120,
            },
            {
                Header: <FormattedMessage id="nomenclatureitem-code" defaultMessage="Code" />,
                accessor: 'code',
                minWidth: 120,
            },
            {
                Header: <FormattedMessage id="nomenclatureitem-is-active" defaultMessage="Is active" />,
                accessor: 'is_active',
                minWidth: 70,
                Cell: BooleanColumn,
            },
        ];

        if (nomenclatureItemFields) {
            nomenclatureItemFields.forEach(([name, field]) => {
                if (
                    ['created', 'modified', 'is_active', 'data', 'translations', 'code'].includes(name)
                    || field.type === 'json'
                ) {
                    return;
                }

                const col = {
                    Header: field.ui.label,
                    accessor: name,
                    minWidth: 120,
                    sortable: false,
                };
                if (field.type === 'boolean') {
                    col.Cell = BooleanColumn;
                } else if (field.type === 'choice' && !field.validation.many) {
                    col.Cell = ({ value }) => {
                        if (!value) {
                            return '';
                        }

                        const choice = field.ui.choices[value];
                        return choice || '';
                    };
                } else if (field.type === 'choice' && field.validation.many) {
                    col.Cell = ({ value }) => {
                        if (value && value.length) {
                            return value.map((v) => {
                                const choice = field.ui.choices[v];
                                return choice || '';
                            }).filter((v) => (!!v)).join(', ');
                        }
                        return '';
                    };
                }

                cols.push(col);
            });
        }

        cols.push({
            actions,
            Cell: ActionsColumn,
            minWidth: 70,
        });

        return cols;
    }, [nomenclatureItemFields, objectId, reloadData]);

    const newObjectUrl = `/administration/nomenclatures/types/${objectId}/dashboard/items/new`;
    const editObjectUrl = `/administration/nomenclatures/types/${objectId}/dashboard/items/update/:id`;

    return nomenclatureType && (
        <>
            <NomenclatureTypeSidePanel
                object={nomenclatureType}
                reload={reloadNomenclatureType}
                panelId={NOMENCLATURE_SIDEPANEL_KEY}
            />
            <ListPage
                ref={listRef}
                {...props}
                resourceName="nomenclatureType.items"
                permissions={['nomenclatures.view_nomenclaturetype']}
                resourceArguments={[objectId]}
                tableId="administration-nomenclatures-items-table"
                columnSettingsFormComponent={ColumnSettingsFormComponent}
                columns={columns}
                newObjectLabel={(
                    <FormattedMessage
                        id="nomenclatureitem-new-nomenclature-item"
                        defaultMessage="New Nomenclature item"
                    />
                )}
                editObjectLabel={(
                    <FormattedMessage
                        id="nomenclatureitem-edit-nomenclature-item"
                        defaultMessage="Edit Nomenclature item"
                    />
                )}
                editObjectUrl={editObjectUrl}
                createRoute={newObjectUrl}
                newObjectUrl={newObjectUrl}
                saveAndNewOption
            />
        </>
    );
};


const mapDispatchToProps = ((dispatch) => (bindActionCreators({ addBreadcrumbs, removeBreadcrumb }, dispatch)));

export default withRouter(connect(null, mapDispatchToProps)(NomenclatureTypeDashboard));
